import { startOfDay, endOfDay, subDays, startOfMonth, endOfMonth, subMonths, startOfYear, endOfYear, subYears, format, parseISO } from "date-fns";
import type { DatePreset } from "@/components/molecules/ManualWidgetForm";

export interface DateRangeValue {
    from: Date;
    to: Date;
}

export const toStartOfDay = startOfDay;
export const toEndOfDay = endOfDay;
export const formatDate = (date: Date) => format(date, "yyyy-MM-dd");

export const formatWidgetDateRangeLabel = (startDate?: string | null, endDate?: string | null): string | null => {
    if (!startDate || !endDate) return null;

    const start = parseISO(startDate);
    const end = parseISO(endDate);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;

    return `${format(start, "MMM d, yyyy")} - ${format(end, "MMM d, yyyy")}`;
};

export const getDateRangeForPreset = (preset?: DatePreset | string | null): DateRangeValue => {
    const today = new Date();
    const end = endOfDay(today);

    // Handle dynamic "last_N_months" presets
    const lastNMonthsMatch = typeof preset === "string" ? preset.match(/^last_(\d+)_months$/) : null;
    const lastNMonths = lastNMonthsMatch ? parseInt(lastNMonthsMatch[1], 10) : null;

    if (lastNMonths && lastNMonths > 0) {
        const startMonth = subMonths(today, lastNMonths);
        const lastMonth = subMonths(today, 1);
        return { from: startOfMonth(startMonth), to: endOfMonth(lastMonth) };
    }

    const normalizedPreset: DatePreset | undefined = (() => {
        switch (preset) {
            case "previous_month":
                return "last_month";
            case "last_3_months":
                return "last_90_days";
            case "last_12_months":
            case "yesterday":
            case "last_7_days":
            case "last_14_days":
            case "last_30_days":
            case "last_90_days":
            case "this_month":
            case "last_month":
            case "this_year":
            case "last_year":
                return preset as DatePreset;
            default:
                return undefined;
        }
    })();

    switch (normalizedPreset) {
        case "yesterday":
            return { from: startOfDay(today), to: end };
        case "last_7_days":
            return { from: startOfDay(subDays(today, 6)), to: end };
        case "last_14_days":
            return { from: startOfDay(subDays(today, 13)), to: end };
        case "last_30_days":
            return { from: startOfDay(subDays(today, 29)), to: end };
        case "last_90_days":
            return { from: startOfDay(subDays(today, 89)), to: end };
        case "last_12_months":
            return { from: startOfDay(subDays(today, 364)), to: end };
        case "this_month":
            return { from: startOfMonth(today), to: endOfMonth(today) };
        case "last_month": {
            const lastMonth = subMonths(today, 1);
            return { from: startOfMonth(lastMonth), to: endOfMonth(lastMonth) };
        }
        case "this_year":
            return { from: startOfYear(today), to: endOfYear(today) };
        case "last_year": {
            const lastYear = subYears(today, 1);
            return { from: startOfYear(lastYear), to: endOfYear(lastYear) };
        }
        default:
            return { from: startOfDay(subDays(today, 29)), to: end };
    }
};
